import { type ReactNode } from 'react';
import * as React from 'react';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { ReactComponent as ArrowRight } from '../../images/icons/icon-arrow-right.svg';
import { ReactComponent as HintIcon } from '../../images/icons/icon-hint.svg';

const HINT_MARGIN = 50;

const Arrow = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
`;

const Dot = styled.div`
  flex: none;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.borderColor.DEFAULT};
  box-shadow:
    0 0 0 2px white,
    0 0 0 5px ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.15)};
`;

const Line = styled.div`
  flex: 1 1 auto;
  height: 1px;
  background-color: ${({ theme }) => theme.borderColor.DEFAULT};
`;

const Pointer = styled.div`
  display: flex;
  align-items: center;
  width: 10px;
  height: 7px;
  flex: none;
`;

const Inner = styled.div`
  background-color: ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.07)};
  padding: 25px 35px;
  border-left: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  color: ${({ theme }) => theme.fontColor.DEFAULT};
  position: absolute;
  top: -29px;
  left: calc(100% + ${HINT_MARGIN}px);

  // This calc kind of assumes the layout is balanced, but it's skewed
  // in the direction of the aside nav. Adding an offset when hints
  // are on the right to account for this.
  --width-offset: 120px;
  width: calc(
    ((100vw - var(--width-offset) - ${({ theme }) => theme.maxWidth.BLOG}) / 2) -
      (${HINT_MARGIN}px * 2)
  );
  max-width: 500px;

  p {
    word-break: break-word;
    font-size: ${({ theme }) => theme.fontSize.DEFAULT}!important;
    line-height: ${({ theme }) => theme.lineHeight.DEFAULT} !important;
  }

  code {
    background-color: ${({ theme }) => theme.borderColor.MUTED} !important;
    color: ${({ theme }) => theme.fontColor.DEFAULT} !important;
    font-size: ${({ theme }) => theme.fontSize.SMALL} !important;
  }

  a {
    color: ${({ theme }) => theme.fontColor.DEFAULT} !important;
  }

  *:last-child {
    margin-bottom: 0 !important;
  }
`;

const Icon = styled.div`
  position: absolute;
  top: 21px;
  left: -18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  background-color: white;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  box-shadow:
    0 0 0 2px white,
    0 0 0 3px ${({ theme }) => theme.borderColor.DEFAULT};
`;

const Wrapper = styled.div<{ $position: 'left' | 'right' }>`
  display: flex;
  position: relative;
  height: 10px;
  margin: 0 0 25px;

  ${({ $position }) =>
    $position === 'left' &&
    css`
      ${Arrow} {
        flex: none;
        flex-direction: row-reverse;
        width: 40px;
        margin-left: -35px;

        ${Pointer} {
          transform: rotate(180deg);
        }
      }

      ${Inner} {
        --width-offset: 0px;
        left: auto;
        right: calc(100% + ${HINT_MARGIN}px);
      }
    `}

  @media (max-width: 1450px) {
    height: auto;

    ${Arrow} {
      display: none;
    }

    ${Inner} {
      position: relative;
      top: auto;
      left: auto !important;
      right: auto !important;
      width: auto;
      max-width: none;
    }
  }
`;

interface BlogHintProps {
  children: ReactNode;
  position?: 'left' | 'right';
}

export const BlogHint: React.ComponentType<BlogHintProps> = React.memo(
  ({ children, position = 'right' }) => {
    return (
      <Wrapper $position={position}>
        <Arrow>
          <Dot />
          <Line />
          <Pointer>
            <ArrowRight />
          </Pointer>
        </Arrow>
        <Inner>
          <Icon>
            <HintIcon />
          </Icon>
          {children}
        </Inner>
      </Wrapper>
    );
  }
);
